import { useQueryClient } from '@tanstack/react-query';
import { Client, Conversation } from '@twilio/conversations';
import { useEffect, useRef, useState } from 'react';
import { useConversationTokenQuery } from '../features/chat/chat.queries';

export const useTwilioConversation = (
  appointmentId: string | undefined,
  onNewMessage: () => void,
  onConnectionError?: (error: string) => void
): { isConnected: boolean } => {
  const [isConnected, setIsConnected] = useState(false);
  const clientRef = useRef<Client | null>(null);
  const conversationRef = useRef<Conversation | null>(null);
  const onNewMessageRef = useRef(onNewMessage);
  const onConnectionErrorRef = useRef(onConnectionError);
  const queryClient = useQueryClient();

  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  useEffect(() => {
    onConnectionErrorRef.current = onConnectionError;
  }, [onConnectionError]);

  const { data: tokenData } = useConversationTokenQuery(appointmentId);

  // Invalidar el cache del token al desmontar el componente (cierre del modal)
  // para que la próxima apertura siempre obtenga un token fresco
  useEffect(() => {
    return () => {
      void queryClient.invalidateQueries({ queryKey: ['conversation-token', appointmentId] });
    };
  }, [appointmentId, queryClient]);

  useEffect(() => {
    if (!tokenData?.token || !tokenData?.conversationSid) return;

    let cancelled = false;

    const setup = async (): Promise<void> => {
      try {
        const client = new Client(tokenData.token);
        clientRef.current = client;

        await new Promise<void>((resolve, reject) => {
          client.on('stateChanged', (state) => {
            if (state === 'initialized') resolve();
            if (state === 'failed') reject(new Error('Twilio Client failed to initialize'));
          });
        });

        if (cancelled) return;

        const conversation = await client.getConversationBySid(tokenData.conversationSid);
        conversationRef.current = conversation;

        conversation.on('messageAdded', () => {
          if (!cancelled) onNewMessageRef.current();
        });

        if (!cancelled) setIsConnected(true);
      } catch (error: any) {
        console.error('[TwilioConversation] Error setting up:', error);
        if (!cancelled) {
          setIsConnected(false);
          // Si es Forbidden, el token cacheado es inválido — invalidar para forzar refetch
          const isForbidden =
            error?.message?.includes('Forbidden') ||
            error?.message?.includes('forbidden') ||
            error?.code === 20101 ||
            error?.status === 403;
          if (isForbidden) {
            void queryClient.invalidateQueries({ queryKey: ['conversation-token', appointmentId] });
          }
          onConnectionErrorRef.current?.(
            'No se pudo establecer conexión en tiempo real. Verifique su conexión a internet.'
          );
        }
      }
    };

    void setup();

    return () => {
      cancelled = true;
      setIsConnected(false);
      conversationRef.current?.removeAllListeners();
      conversationRef.current = null;
      void clientRef.current?.shutdown();
      clientRef.current = null;
    };
  }, [tokenData?.token, tokenData?.conversationSid, appointmentId, queryClient]);

  return { isConnected };
};
